import {normalizeVariationStatusKey, VariationStatusKey} from '../../projectsetup/models'

export type PcgVariationStatusItem = {
  id: string
  name?: string
  statusKey?: VariationStatusKey | string
}

export type PcgVariationStatusRow = {
  statusAmounts?: Record<string, number | null>
  statusLabel?: string | null
  isTotalRow?: boolean
}

export type PcgVariationTotals = {
  statusTotals: Record<string, number>
  total: number
}

const MAXIMUM_PAYMENT_STATUS_PRIORITY: VariationStatusKey[] = [
  'endorsed',
  'recommended',
  'submitted',
  'estimated',
]

const toNumber = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

const roundCurrency = (value: number) => Number(value.toFixed(2))

const createEmptyTotals = (statusItems: PcgVariationStatusItem[]): PcgVariationTotals => {
  const statusTotals: Record<string, number> = {}
  statusItems.forEach((status) => {
    if (status.id) {
      statusTotals[status.id] = 0
    }
  })
  return {statusTotals, total: 0}
}

const normalizeStatusToken = (value?: string | null) =>
  (value ?? '')
    .replace(/[^a-z0-9]+/gi, '')
    .trim()
    .toLowerCase()

export const normalizePcgVariationStatusKey = (
  value?: string | null
): VariationStatusKey | undefined => {
  const normalized = normalizeStatusToken(value)
  if (!normalized) {
    return undefined
  }
  if (normalized === 'approved') {
    return 'endorsed'
  }
  if (
    normalized.includes('pcgsuperintendentendorsed') ||
    normalized.includes('endorsed') ||
    normalized.includes('approved')
  ) {
    return 'endorsed'
  }
  if (normalized.includes('dcwcrecommended') || normalized.includes('recommended')) {
    return 'recommended'
  }
  if (normalized.includes('submitted')) {
    return 'submitted'
  }
  if (normalized.includes('estimated')) {
    return 'estimated'
  }
  if (normalized.includes('rejected') || normalized.includes('withdrawn')) {
    return 'rejected'
  }
  return normalizeVariationStatusKey(value)
}

export const isRejectedOrWithdrawnPcgVariationStatus = (value?: string | null) =>
  normalizePcgVariationStatusKey(value) === 'rejected'

export const isRejectedOrWithdrawnPcgVariationStatusItem = (
  status?: PcgVariationStatusItem | null
) =>
  Boolean(
    status &&
      [status.id, status.name, status.statusKey].some((value) =>
        isRejectedOrWithdrawnPcgVariationStatus(value)
      )
  )

const buildStatusIndexes = (statusItems: PcgVariationStatusItem[]) => {
  const idByToken = new Map<string, string>()
  const idByStatusKey = new Map<VariationStatusKey, string>()

  statusItems.forEach((status) => {
    if (!status.id) {
      return
    }

    const candidates = [status.id, status.name, status.statusKey]
    candidates.forEach((candidate) => {
      const token = normalizeStatusToken(candidate)
      if (token) {
        idByToken.set(token, status.id)
      }

      const key = normalizePcgVariationStatusKey(candidate)
      if (key) {
        idByStatusKey.set(key, status.id)
      }
    })
  })

  return {idByToken, idByStatusKey}
}

const resolveFallbackStatusIdFromAmounts = (
  amounts: Record<string, number | null> | undefined,
  statusItems: PcgVariationStatusItem[]
) => {
  const statusIds = new Set(statusItems.map((status) => status.id))
  const nonZero = Object.entries(amounts ?? {}).filter(
    ([id, value]) => statusIds.has(id) && toNumber(value) !== 0
  )

  return nonZero.length === 1 ? nonZero[0][0] : undefined
}

export const resolveVariationCurrentStatusId = (
  row: PcgVariationStatusRow,
  statusItems: PcgVariationStatusItem[]
) => {
  const {idByToken, idByStatusKey} = buildStatusIndexes(statusItems)
  const labelToken = normalizeStatusToken(row.statusLabel)
  if (labelToken) {
    const direct = idByToken.get(labelToken)
    if (direct) {
      return direct
    }

    const key = normalizePcgVariationStatusKey(row.statusLabel)
    if (key) {
      const byKey = idByStatusKey.get(key)
      if (byKey) {
        return byKey
      }
    }
  }

  return resolveFallbackStatusIdFromAmounts(row.statusAmounts, statusItems)
}

export const isRejectedOrWithdrawnPcgVariationRow = (
  row: PcgVariationStatusRow,
  statusItems: PcgVariationStatusItem[] = []
) => {
  if (isRejectedOrWithdrawnPcgVariationStatus(row.statusLabel)) {
    return true
  }

  const statusId = resolveVariationCurrentStatusId(row, statusItems)
  if (statusId) {
    const status = statusItems.find(
      (item) => item.id?.toLowerCase() === statusId.toLowerCase()
    )
    if (
      isRejectedOrWithdrawnPcgVariationStatus(statusId) ||
      isRejectedOrWithdrawnPcgVariationStatusItem(status)
    ) {
      return true
    }
  }

  const nonZeroStatusKeys = Object.entries(row.statusAmounts ?? {})
    .filter(([, value]) => toNumber(value) !== 0)
    .map(([key]) => key)

  return (
    nonZeroStatusKeys.length === 1 &&
    isRejectedOrWithdrawnPcgVariationStatus(nonZeroStatusKeys[0])
  )
}

export const buildCurrentStatusAmountTotals = <T extends PcgVariationStatusRow>(
  rows: T[] = [],
  statusItems: PcgVariationStatusItem[] = [],
  resolveAmount?: (row: T, statusId: string) => number
): PcgVariationTotals => {
  const totals = createEmptyTotals(statusItems)
  rows
    .filter((row) => !row.isTotalRow)
    .forEach((row) => {
      const statusId = resolveVariationCurrentStatusId(row, statusItems)
      if (!statusId || totals.statusTotals[statusId] === undefined) {
        return
      }

      const amount = roundCurrency(
        resolveAmount ? resolveAmount(row, statusId) : toNumber(row.statusAmounts?.[statusId])
      )
      totals.statusTotals[statusId] = roundCurrency(totals.statusTotals[statusId] + amount)
      totals.total = roundCurrency(totals.total + amount)
    })

  return totals
}

export const buildCurrentStatusAmountTotalsByKey = <T extends PcgVariationStatusRow>(
  rows: T[] = [],
  statusItems: PcgVariationStatusItem[] = [],
  resolveKey: (row: T) => string | null | undefined,
  resolveAmount?: (row: T, statusId: string) => number
): Map<string, PcgVariationTotals> => {
  const groupedTotals = new Map<string, PcgVariationTotals>()

  rows
    .filter((row) => !row.isTotalRow)
    .forEach((row) => {
      const key = (resolveKey(row) ?? '').trim()
      if (!key) {
        return
      }

      const statusId = resolveVariationCurrentStatusId(row, statusItems)
      if (!statusId) {
        return
      }

      const totals = groupedTotals.get(key) ?? createEmptyTotals(statusItems)
      if (totals.statusTotals[statusId] === undefined) {
        return
      }

      const amount = roundCurrency(
        resolveAmount ? resolveAmount(row, statusId) : toNumber(row.statusAmounts?.[statusId])
      )
      totals.statusTotals[statusId] = roundCurrency(totals.statusTotals[statusId] + amount)
      totals.total = roundCurrency(totals.total + amount)
      groupedTotals.set(key, totals)
    })

  return groupedTotals
}

export const deriveMaximumPaymentValue = (
  statusAmounts: Record<string, number | null> | undefined,
  statusItems: PcgVariationStatusItem[]
) => {
  const {idByStatusKey} = buildStatusIndexes(statusItems)

  for (const statusKey of MAXIMUM_PAYMENT_STATUS_PRIORITY) {
    const statusId = idByStatusKey.get(statusKey)
    if (!statusId) {
      continue
    }

    const amount = toNumber(statusAmounts?.[statusId])
    if (amount !== 0) {
      return roundCurrency(amount)
    }
  }

  return null
}

export const buildMaximumPaymentTotal = <T extends PcgVariationStatusRow>(
  rows: T[] = [],
  statusItems: PcgVariationStatusItem[] = []
) =>
  roundCurrency(
    rows
      .filter((row) => !row.isTotalRow)
      .reduce(
        (total, row) => total + toNumber(deriveMaximumPaymentValue(row.statusAmounts, statusItems)),
        0
      )
  )
